/**
 * @description Renders the form's footer in the footer section of the application page.
 */

import React from 'react';
import * as ReactDOM from 'react-dom';
import { PropTypes } from 'prop-types';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import { FooterContext } from '@seccon/core/contexts/footer';
import Spinner from '@seccon/shared/ui/components/Spinner';

const styles = {
  actionButton: {
    minWidth: '100px'
  },
  container: {
    display: 'flex',
    flexWrap: 'nowrap',
    backgroundColor: '#fff',
    borderColor: 'grey.400',
    borderTop: 1,
    boxSizing: 'border-box',
    height: '86px',
    p: '24px',
    width: '100%',
    justifyContent: 'space-between'
  },
  leftContainer: {
    display: 'flex',
    flexWrap: 'nowrap'
  },
  rightContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    flexDirection: 'row-reverse',
    gap: '8px'
  }
};

/**
 * @prop disabled {boolean} - indicates if the submit button should be disabled
 * @prop isSubmitting {boolean} - indicates if the form is currently submitting
 * @prop onCancel {Function} - callback function for when the cancel button is clicked
 * @prop onSubmit {Function} - callback function for when the submit button is clicked
 */
const FormFooter = ({
  disabled,
  isSubmitting,
  onBack,
  onCancel,
  onSubmit
}) => {
  // Retrieve a reference to the page's footer, so the form footer can be generated in the right place
  const footerEl = React.useContext(FooterContext);

  // If the footer is not defined, do nothing
  if (footerEl === null) {
    return null;
  }

  const content = (
    <Box sx={styles.container}>
      <Box sx={styles.leftContainer}>
        <Button onClick={onBack} variant='outlined' sx={styles.actionButton}>
          Back
        </Button>
      </Box>
      <Box sx={styles.rightContainer}>
        <Button onClick={onSubmit} variant='contained' sx={styles.actionButton} disabled={disabled}>
          {isSubmitting ? <Spinner /> : 'Sync'}
        </Button>
        <Button onClick={onCancel} variant='outlined' sx={styles.actionButton}>
          Cancel
        </Button>
      </Box>
    </Box>
  );

  return ReactDOM.createPortal(content, footerEl);
};

FormFooter.propTypes = {
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

FormFooter.defaultProps = {
  disabled: false,
  isSubmitting: false
};

export default FormFooter;
